import { getAccessToken } from '@/api/http/tokens'

const CHUNK_SIZE = 8 * 1024 * 1024 // 8MB

interface ChunkedOptions {
  folderId?: number
  onProgress?: (pct: number, speed: string) => void
  signal?: AbortSignal
}

/**
 * Chunked upload for large files (>=100MB).
 * Backend creates the document on init, uploads chunks,
 * then completes → assembles → stores file → creates version.
 */
export async function uploadChunked(
  projectId: number,
  file: File,
  options: ChunkedOptions = {},
): Promise<void> {
  const token = getAccessToken() || ''
  if (!token) throw new Error('not authenticated')

  const totalChunks = Math.ceil(file.size / CHUNK_SIZE)
  const jsonHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }

  // 1. Initiate — backend creates a draft document + upload session
  const initResp = await fetch('/api/v1/documents/chunked/init', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({
      project_id: projectId,
      folder_id: options.folderId,
      filename: file.name,
      file_size: file.size,
      total_chunks: totalChunks,
    }),
  })
  if (!initResp.ok) {
    const body = await initResp.text()
    throw new Error(`init failed: ${body}`)
  }
  const { data: { id: sessionId } } = await initResp.json()

  // 2. Upload chunks sequentially
  let uploadedBytes = 0
  let lastTime = Date.now()
  let lastLoaded = 0

  for (let i = 0; i < totalChunks; i++) {
    if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError')

    const start = i * CHUNK_SIZE
    const end = Math.min(start + CHUNK_SIZE, file.size)
    const chunk = file.slice(start, end)

    const formData = new FormData()
    formData.append('file', chunk, file.name)

    const chunkResp = await fetch(`/api/v1/documents/chunked/${sessionId}/${i + 1}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
      signal: options.signal,
    })
    if (!chunkResp.ok) {
      const body = await chunkResp.text()
      throw new Error(`chunk ${i + 1} failed: ${body}`)
    }

    uploadedBytes += chunk.size
    const pct = Math.round((uploadedBytes / file.size) * 100)

    if (options.onProgress) {
      const now = Date.now()
      const elapsed = (now - lastTime) / 1000
      let speed = ''
      if (elapsed > 0.3) {
        const bps = (uploadedBytes - lastLoaded) / elapsed
        speed = bps >= 1024 * 1024
          ? `${(bps / 1024 / 1024).toFixed(1)} МБ/с`
          : `${(bps / 1024).toFixed(0)} КБ/с`
        lastTime = now
        lastLoaded = uploadedBytes
      }
      options.onProgress(pct, speed)
    }
  }

  // 3. Complete — backend assembles chunks, stores file, creates version
  const completeResp = await fetch(`/api/v1/documents/chunked/${sessionId}/complete`, {
    method: 'POST',
    headers: jsonHeaders,
  })
  if (!completeResp.ok) {
    const body = await completeResp.text()
    throw new Error(`complete failed: ${body}`)
  }
}
